import { ReactNode, ReactElement, isValidElement, cloneElement } from "react";

interface Props {
  content: ReactNode;
  children: ReactElement;
  /** Which side of the trigger the panel opens on. Default "top". */
  position?: "top" | "bottom";
  /** Horizontal alignment of the panel relative to the trigger. Default "center". */
  align?: "left" | "center" | "right";
  /**
   * Use `content` as the trigger's accessible name (aria-label) — for an
   * icon-only trigger with no visible text of its own, mirroring what the
   * native `title` attribute this replaced used to provide for free. Leave
   * unset when the trigger already has its own visible text (a button
   * labeled "Add Record", say) — aria-label would override that text
   * instead of supplementing it.
   */
  asLabel?: boolean;
  className?: string;
}

/**
 * App-wide hover tooltip — generalizes the group/group-hover panel pattern
 * already used (duplicated) in CompanyApplyChip/RemoteScopeChip, for a plain
 * text/short-content tooltip instead of those two's bespoke rich panels.
 */
export default function Tooltip({ content, children, position = "top", align = "center", asLabel, className }: Props) {
  // The gap between trigger and panel is padding *inside* this element's own
  // hoverable box (pb-2/pt-2, not a margin outside it) — otherwise moving the
  // mouse from the trigger toward the panel crosses a dead zone that drops
  // group-hover and hides the panel before the cursor arrives.
  const vertical = position === "top" ? "bottom-full pb-2" : "top-full pt-2";
  const horizontal = align === "left" ? "left-0" : align === "right" ? "right-0" : "left-1/2 -translate-x-1/2";

  const trigger = asLabel && typeof content === "string" && isValidElement(children)
    ? cloneElement(children, { "aria-label": content } as Record<string, unknown>)
    : children;

  return (
    <span className={`group relative inline-flex ${className ?? ""}`}>
      {trigger}
      <span className={`hidden group-hover:block absolute z-30 ${vertical} ${horizontal} w-max max-w-xs`}>
        <span className="block bg-tooltip text-tooltip-foreground rounded-lg shadow-lg px-3 py-2 text-left text-[11px] leading-snug whitespace-normal">
          {content}
        </span>
      </span>
    </span>
  );
}
